/**
 * Deterministic avatar colours (Figma-confirmed 5-colour palette + a neutral
 * gray for new/invited users). Colour is assigned so the same user renders the
 * same colour in every view.
 *
 * The index is a digest of the user id — deliberately *not* the join-date rank
 * the story sketched, because a rank shifts for everyone below a member who
 * gets removed.
 *
 * It is computed here, in the browser, and nowhere else: an avatar palette is a
 * presentation concern that no identity provider has an opinion about. That
 * also means it needs no data to arrive before it is right, so a user's colour
 * never changes between first paint and a settled query.
 */

export interface AvatarColor {
  bg: string;
  text: string;
}

export const AVATAR_PALETTE: readonly AvatarColor[] = [
  { bg: 'var(--au-secondary-orange-or-6)', text: 'var(--au-text-inverse)' },
  { bg: 'var(--au-success-dark)', text: 'var(--au-text-inverse)' },
  { bg: 'var(--au-warning-dark)', text: 'var(--au-text-inverse)' },
  { bg: 'var(--au-primary-blue)', text: 'var(--au-text-inverse)' },
  { bg: 'var(--au-info)', text: 'var(--au-text-inverse)' },
];

/** Neutral swatch for a not-yet-provisioned / invited member. */
export const NEW_USER_AVATAR_COLOR: AvatarColor = {
  bg: 'var(--au-info-grayscale-gy-100)',
  text: 'var(--au-info-grayscale-gy-400)',
};

/** Stable hash of an id → palette index. */
export const avatarColorIndex = (id: string): number => {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    // Math.imul keeps the running hash within 32-bit range without bitwise ops.
    hash = Math.imul(hash, 31) + id.charCodeAt(i);
  }
  return Math.abs(hash) % AVATAR_PALETTE.length;
};

/** Resolve a palette entry for an index (wraps into range). */
export const avatarColorForIndex = (index: number): AvatarColor => {
  const size = AVATAR_PALETTE.length;
  return AVATAR_PALETTE[((index % size) + size) % size];
};
